<template>
  <div data-component-group="business-form" data-component="comments">
    <slot name="beforeForm" />

    <Can action="change-comments-status" subject="business">
      <div v-if="!isDisabled" class="component-header">
        <v-autocomplete
          v-model="country"
          :items="threadSubmissions"
          prepend-inner-icon="public"
          label="Country"
          item-title="name"
          item-value="code"
          :return-object="true"
          required
          :readonly="isDisabled"
          :error-messages="countryErrors"
          @blur="v$.country.$touch()"
        />

        <div class="component-actions">
          <v-btn
            color="secondary"
            :loading="isLoading"
            @click="onUpdateComments">
            <v-icon>edit</v-icon>
            {{ triggerLabel }}
          </v-btn>
        </div>
      </div>
    </Can>
    <div class="component-body">
      <Removables
        v-if="hasComments"
        :items="comments"
        :is-disabled="
          isDisabled || !$ability.can('change-comments-status', 'business')
        "
        :on-remove="onRemoveComments"
      >
        <template v-slot:item="scope" >
          <div class="d-flex align-center">
            <v-icon> public </v-icon>
            {{ scope.item.name }}
          </div>
        </template>
      </Removables>
    </div>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import Removables from '../../lists/Removables';
  import { isStr } from '../../../utils';
  import { find } from 'lodash-es';
  // TODO: Translate component

  export default {
    components: {
      Removables,
    },
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        country: {
          required,
        },
      };
    },
    props: {
      business: {
        type: Object,
        default: undefined,
      },
      triggerLabel: {
        type: String,
        default: 'Attach',
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onSubmit: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        country: null,
        isLoading: false,
      };
    },
    computed: {
      userCountries() {
        return this.$store.getters['users/countries'];
      },
      threadSubmissions() {
        const { threadSubmissions } = this.$store.getters['businesses/activeItem'] || {};

        return threadSubmissions?.filter((item) => {
          return find(this.userCountries, { code: item.code });
        });
      },
      comments() {
        return this.$store.getters['businesses/activeItem']?.comments ?? [];
      },
      hasComments() {
        return this.comments.length > 0;
      },
      countryErrors() {
        return this.v$.country.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Country is required';
          }
          return error.$message;
        });
      },
    },
    watch: {
      'business.id': {
        immediate: true,
        handler(nextId, prevId) {
          if (nextId) {
            if (prevId !== undefined) {
              this.clear();
            }
            this.getComments(this.business);
          }
        }
      },
    },
    methods: {
      onUpdateComments() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        this.isLoading = true;
        const { country, onSubmit } = this;
        onSubmit(country)
          .then((res) => {
            this.clear();
            return res;
          })
          .catch(err=>err)
          .finally(()=>{
            this.isLoading = false;
          });
      },
      canSubmitRequest() {
        const { country } = this;

        return isStr(country?.code);
      },
      getComments({ id }) {
        const query = { id };
        return this.$store.dispatch('businesses/getCommentCountries', query);
      },
      onRemoveComments(commentGroup) {
        const query = {
          id: this.business.id,
          country: commentGroup.code,
        };

        return this.$store.dispatch('businesses/removeCommentCountry', query);
      },
      clear() {
        this.v$.$reset();
        this.country = null;
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='business-form'][data-component='comments'] {
  padding: $spacing-xl;
  background-color: rgb(var(--v-theme-surface));
  border-top: 1px solid map.get($colors, grey, lighten-2);

  .component-actions {
    text-align: right;
    padding: 0 0 16px 0;

    .v-btn {
      margin: 0;

      .v-icon {
        margin: 0 6px 0 0;
      }
    }
  }

  .labels {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
  }

  .label {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: $text-size-sm;
    padding: 2px 6px;
    margin: 0 10px 10px 0;
    border-radius: $border-radius;
    color: map.get($colors, shades, 'white');
    background-color: map.get($colors, primary);

    .v-icon {
      font-size: $text-size-md;
      margin: 0 6px 0 0;
      color: map.get($colors, shades, 'white');
    }
  }

  [data-component='removables'] {
    padding: 16px 0 0 0;

    .v-list {
      padding: 0;
    }

    .v-list-item {
      height: 62px;
      border-radius: $border-radius;
      background-color: rgba(var(--v-theme-on-surface), 0.06);
    }
  }
}
</style>
